import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, AlertTriangle, ShieldCheck, TrendingUp } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { format, parseISO, subDays } from "date-fns";

interface DailyStats {
  date: string;
  sent: number;
  bounced: number;
  rate: number;
}

interface DomainBounce {
  domain: string;
  count: number;
}

const EmailHealthWidget = () => {
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [topDomains, setTopDomains] = useState<DomainBounce[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const since = subDays(new Date(), 14).toISOString();
      const { data: logs } = await supabase
        .from("email_send_log")
        .select("status, recipient_email, created_at")
        .gte("created_at", since)
        .in("status", ["sent", "bounced", "complained"]);

      if (!logs) { setLoading(false); return; }

      // Daily delivery rate trend
      const byDay = new Map<string, { sent: number; failed: number }>();
      const domainMap = new Map<string, number>();

      for (const log of logs) {
        const day = format(parseISO(log.created_at), "MMM dd");
        const entry = byDay.get(day) || { sent: 0, failed: 0 };
        if (log.status === "sent") entry.sent++;
        else entry.failed++;
        byDay.set(day, entry);

        if (log.status === "bounced" || log.status === "complained") {
          const domain = log.recipient_email.split("@")[1] || "unknown";
          domainMap.set(domain, (domainMap.get(domain) || 0) + 1);
        }
      }

      const daily: DailyStats[] = [];
      for (let i = 13; i >= 0; i--) {
        const d = format(subDays(new Date(), i), "MMM dd");
        const entry = byDay.get(d) || { sent: 0, failed: 0 };
        const total = entry.sent + entry.failed;
        daily.push({
          date: d,
          sent: entry.sent,
          bounced: entry.failed,
          rate: total > 0 ? Math.round((entry.sent / total) * 100) : 100,
        });
      }

      const domains = Array.from(domainMap.entries())
        .map(([domain, count]) => ({ domain, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setDailyStats(daily);
      setTopDomains(domains);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card className="border-border">
        <CardContent className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const totalSent = dailyStats.reduce((s, d) => s + d.sent, 0);
  const totalBounced = dailyStats.reduce((s, d) => s + d.bounced, 0);
  const totalEmails = totalSent + totalBounced;
  const overallRate = totalEmails > 0 ? Math.round((totalSent / totalEmails) * 100) : 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Mail size={18} className="text-primary" />
        <h3 className="font-display text-lg">Email Health</h3>
        <Badge variant="outline" className="ml-auto text-xs">Last 14 days</Badge>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={14} className="text-primary" />
              <span className="text-xs text-muted-foreground">Delivery Rate</span>
            </div>
            <p className="text-2xl font-semibold">{overallRate}%</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} className="text-primary" />
              <span className="text-xs text-muted-foreground">Delivered</span>
            </div>
            <p className="text-2xl font-semibold">{totalSent}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={14} className="text-destructive" />
              <span className="text-xs text-muted-foreground">Bounced</span>
            </div>
            <p className="text-2xl font-semibold">{totalBounced}</p>
          </CardContent>
        </Card>
      </div>

      {/* Delivery rate trend */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Delivery Rate Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyStats}>
                <defs>
                  <linearGradient id="emailHealthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  formatter={(value: number) => [`${value}%`, "Delivery Rate"]}
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="hsl(var(--primary))"
                  fill="url(#emailHealthGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top bouncing domains */}
      {topDomains.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Top Bouncing Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topDomains} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="domain" type="category" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="count" name="Bounces" radius={[0, 4, 4, 0]}>
                    {topDomains.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? "hsl(var(--destructive))" : "hsl(var(--muted-foreground))"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailHealthWidget;
